import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Building2, Sparkles, Phone, ArrowRight, ChevronDown, UtensilsCrossed, Shirt, Flame, Refrigerator, SunDim, Microwave, DoorOpen, Warehouse, Wind, BedDouble, Trash2, Fence, Sparkles as SparklesIcon, Package, Minus, Plus } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
import { ServiceNav } from '../components/ServiceNav';
const cardClass = 'bg-white rounded-2xl border border-gray-200 p-8 md:p-10 shadow-sm';
const stepHeadingClass = 'font-bebas text-2xl md:text-3xl text-[#1a2744] pb-3 border-b border-gray-200';
const labelClass = 'text-[#1a2744] font-medium text-sm';
const inputClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all placeholder-gray-400';
const selectClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all appearance-none cursor-pointer';
type PillGroupProps = {
  options: string[];
  selected: string | string[];
  onSelect: (val: string) => void;
  multi?: boolean;
};
function PillGroup({
  options,
  selected,
  onSelect,
  multi = false
}: PillGroupProps) {
  const isSelected = (opt: string) => multi ? (selected as string[]).includes(opt) : selected === opt;
  return <div className="flex flex-wrap gap-3" data-id="element-1342">
      {options.map(opt => <button key={opt} type="button" onClick={() => onSelect(opt)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${isSelected(opt) ? 'bg-[#e91e8c] border-[#e91e8c] text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-1343">
          {opt}
        </button>)}
    </div>;
}
const extras = [{
  name: 'Airbnb Combo Package',
  price: 7,
  icon: Package
}, {
  name: 'Washing dishes',
  price: 7,
  icon: UtensilsCrossed
}, {
  name: 'Laundry (wash & fold)',
  price: 7,
  icon: Shirt
}, {
  name: 'Inside of oven',
  price: 7,
  icon: Flame
}, {
  name: 'Inside of fridge',
  price: 7,
  icon: Refrigerator
}, {
  name: 'Inside facade of windows',
  price: 7,
  icon: SunDim
}, {
  name: 'Inside microwave',
  price: 7,
  icon: Microwave
}, {
  name: 'Interior of Kitchen Cabinets',
  price: 7,
  icon: DoorOpen
}, {
  name: 'Finished Basement',
  price: 7,
  icon: Warehouse
}, {
  name: 'Flu Buster - Eucalyptus',
  price: 7,
  icon: Wind
}, {
  name: 'Change Sheets',
  price: 7,
  icon: BedDouble
}, {
  name: 'Property Cleanup',
  price: 7,
  icon: Trash2
}, {
  name: 'Patio or Balcony',
  price: 7,
  icon: Fence
}, {
  name: 'Carpet Steam Clean - Entire Home',
  price: 7,
  icon: SparklesIcon
}, {
  name: 'Area Rugs Steam Cleaning',
  price: 7,
  icon: SparklesIcon
}];
const timeSlots = ['14:00', '15:00'];
export function PostRenovationQuote() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Step 1 - Contact
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({
      ...contact,
      [e.target.name]: e.target.value
    });
  };
  // Step 2 - Location
  const [location, setLocation] = useState({
    address: '',
    unit: '',
    city: '',
    postalCode: ''
  });
  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocation({
      ...location,
      [e.target.name]: e.target.value
    });
  };
  // Step 3 - Cleaning details
  const [hours, setHours] = useState('2 Hours');
  const [cleaners, setCleaners] = useState('2 Cleaners');
  const [propertyType, setPropertyType] = useState('Cottage/Townhouse/Bungalow');
  const [bedrooms, setBedrooms] = useState('Studio / 1 Bedroom');
  const [bathrooms, setBathrooms] = useState('1 Bathroom');
  const [kitchens, setKitchens] = useState('1 Kitchen');
  const [floors, setFloors] = useState(1);
  const [sqFeet, setSqFeet] = useState('less than 1600');
  const [access, setAccess] = useState('I will meet you at the location');
  const [homeStatus, setHomeStatus] = useState('Completely Empty');
  const [parking, setParking] = useState('Free on street');
  // Step 4 - Extras
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const toggleExtra = (name: string) => {
    setSelectedExtras(prev => prev.includes(name) ? prev.filter(e => e !== name) : [...prev, name]);
  };
  // Step 5 - Date & Time
  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  // Step 6 - Access details
  const [accessDetails, setAccessDetails] = useState('');
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-1344">
      <SEOHead title="Book Post Renovation Cleaning | Smith & Jengs Cleaning Services" description="Book your Post Renovation Cleaning in Bicester and Oxfordshire. Fast, reliable builders clean in 60 seconds." canonical="https://smithandjengs.com/post-renovation-quote" data-id="element-1345" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Post Renovation',
      url: 'https://smithandjengs.com/post-renovation-quote'
    }, {
      name: 'Book Online',
      url: 'https://smithandjengs.com/post-renovation-quote'
    }]} data-id="element-1346" />
      <Navbar data-id="element-1347" />

      <main className="flex-1 pt-24" data-id="element-1348">
        <ServiceNav data-id="element-1349" />

        {/* Header */}
        <section className="w-full bg-white pt-12 md:pt-16 pb-12 px-4 md:px-12" data-id="element-1350">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl w-full mx-auto text-center" data-id="element-1351">
            <h1 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide mb-6" data-id="element-1352">
              <span className="text-5xl md:text-6xl lg:text-7xl block" data-id="element-1353">
                BOOK ONLINE
              </span>
              <span className="text-3xl md:text-4xl lg:text-5xl block text-[#e91e8c]" data-id="element-1354">
                POST RENOVATION
              </span>
            </h1>
            <p className="text-gray-600 font-medium max-w-2xl mx-auto text-lg" data-id="element-1355">
              Book your Post Renovation Cleaning in 60 seconds.
            </p>
          </motion.div>
        </section>

        {/* Form Section */}
        <section className="w-full bg-[#f8f9fa] py-12 md:py-16 px-4 md:px-8" id="booking-form" data-id="element-1356">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="max-w-4xl w-full mx-auto" data-id="element-1357">
            <form className="space-y-8" onSubmit={e => e.preventDefault()} data-id="element-1358">
              {/* STEP 1: Contact */}
              <div className={cardClass} data-id="element-1359">
                <h3 className={stepHeadingClass} data-id="element-1360">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1361">STEP 1:</span> Contact
                  Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1362">
                  <div className="space-y-2" data-id="element-1363">
                    <label className={labelClass} data-id="element-1364">First Name</label>
                    <input type="text" name="firstName" value={contact.firstName} onChange={handleContactChange} className={inputClass} placeholder="John" data-id="element-1365" />
                  </div>
                  <div className="space-y-2" data-id="element-1366">
                    <label className={labelClass} data-id="element-1367">Last Name</label>
                    <input type="text" name="lastName" value={contact.lastName} onChange={handleContactChange} className={inputClass} placeholder="Doe" data-id="element-1368" />
                  </div>
                  <div className="space-y-2" data-id="element-1369">
                    <label className={labelClass} data-id="element-1370">Email</label>
                    <input type="email" name="email" value={contact.email} onChange={handleContactChange} className={inputClass} placeholder="john@example.com" data-id="element-1371" />
                  </div>
                  <div className="space-y-2" data-id="element-1372">
                    <label className={labelClass} data-id="element-1373">Phone Number</label>
                    <div className="flex" data-id="element-1374">
                      <div className="flex items-center justify-center px-4 border border-r-0 border-gray-200 rounded-l-lg bg-gray-50 text-gray-500" data-id="element-1375">
                        🇬🇧
                      </div>
                      <input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} className={`${inputClass} rounded-l-none`} placeholder="07000 000000" data-id="element-1376" />
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 2: Location */}
              <div className={cardClass} data-id="element-1377">
                <h3 className={stepHeadingClass} data-id="element-1378">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1379">STEP 2:</span> Cleaning
                  Location
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1380">
                  <div className="space-y-2" data-id="element-1381">
                    <label className={labelClass} data-id="element-1382">Address</label>
                    <input type="text" name="address" value={location.address} onChange={handleLocationChange} className={inputClass} placeholder="Enter a location" data-id="element-1383" />
                  </div>
                  <div className="space-y-2" data-id="element-1384">
                    <label className={labelClass} data-id="element-1385">Unit/Apt/Suite #</label>
                    <input type="text" name="unit" value={location.unit} onChange={handleLocationChange} className={inputClass} placeholder="Optional" data-id="element-1386" />
                  </div>
                  <div className="space-y-2" data-id="element-1387">
                    <label className={labelClass} data-id="element-1388">City</label>
                    <input type="text" name="city" value={location.city} onChange={handleLocationChange} className={inputClass} placeholder="Bicester" data-id="element-1389" />
                  </div>
                  <div className="space-y-2" data-id="element-1390">
                    <label className={labelClass} data-id="element-1391">Postal Code</label>
                    <input type="text" name="postalCode" value={location.postalCode} onChange={handleLocationChange} className={inputClass} placeholder="OX26" data-id="element-1392" />
                  </div>
                </div>
              </div>

              {/* STEP 3: Details */}
              <div className={cardClass} data-id="element-1393">
                <h3 className={stepHeadingClass} data-id="element-1394">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1395">STEP 3:</span> Tell us
                  about the cleaning
                </h3>
                <div className="space-y-8 mt-8" data-id="element-1396">
                  <div className="space-y-4" data-id="element-1397">
                    <label className={labelClass} data-id="element-1398">
                      How many hours and cleaners would you like to book?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1399">
                      <div className="relative" data-id="element-1400">
                        <select value={hours} onChange={e => setHours(e.target.value)} className={selectClass} data-id="element-1401">
                          <option data-id="element-1402">2 Hours</option>
                          <option data-id="element-1403">3 Hours</option>
                          <option data-id="element-1404">4 Hours</option>
                          <option data-id="element-1405">5 Hours</option>
                          <option data-id="element-1406">6 Hours</option>
                          <option data-id="element-1407">7 Hours</option>
                          <option data-id="element-1408">8 Hours</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1409" />
                      </div>
                      <div className="relative" data-id="element-1410">
                        <select value={cleaners} onChange={e => setCleaners(e.target.value)} className={selectClass} data-id="element-1411">
                          <option data-id="element-1412">1 Cleaner</option>
                          <option data-id="element-1413">2 Cleaners</option>
                          <option data-id="element-1414">3 Cleaners</option>
                          <option data-id="element-1415">4 Cleaners</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1416" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-1417">
                    <label className={labelClass} data-id="element-1418">Property type</label>
                    <div className="relative" data-id="element-1419">
                      <select value={propertyType} onChange={e => setPropertyType(e.target.value)} className={selectClass} data-id="element-1420">
                        <option data-id="element-1421">Cottage/Townhouse/Bungalow</option>
                        <option data-id="element-1422">Detached House</option>
                        <option data-id="element-1423">Semi-Detached</option>
                        <option data-id="element-1424">Flat/Apartment</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1425" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1426">
                    <div className="space-y-2" data-id="element-1427">
                      <label className={labelClass} data-id="element-1428">Bedrooms</label>
                      <div className="relative" data-id="element-1429">
                        <select value={bedrooms} onChange={e => setBedrooms(e.target.value)} className={selectClass} data-id="element-1430">
                          <option data-id="element-1431">Studio / 1 Bedroom</option>
                          <option data-id="element-1432">2 Bedrooms</option>
                          <option data-id="element-1433">3 Bedrooms</option>
                          <option data-id="element-1434">4 Bedrooms</option>
                          <option data-id="element-1435">5+ Bedrooms</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1436" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-1437">
                      <label className={labelClass} data-id="element-1438">Bathrooms</label>
                      <div className="relative" data-id="element-1439">
                        <select value={bathrooms} onChange={e => setBathrooms(e.target.value)} className={selectClass} data-id="element-1440">
                          <option data-id="element-1441">1 Bathroom</option>
                          <option data-id="element-1442">2 Bathrooms</option>
                          <option data-id="element-1443">3 Bathrooms</option>
                          <option data-id="element-1444">4+ Bathrooms</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1445" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-1446">
                    <label className={labelClass} data-id="element-1447">Kitchens</label>
                    <PillGroup options={['1 Kitchen', '2 Kitchens', '3 Kitchens']} selected={kitchens} onSelect={setKitchens} data-id="element-1448" />
                  </div>

                  <div className="space-y-2" data-id="element-1449">
                    <label className={labelClass} data-id="element-1450">
                      # of floors in the home (incl. Basement)?
                    </label>
                    <div className="flex items-center w-full max-w-[200px]" data-id="element-1451">
                      <button type="button" onClick={() => setFloors(Math.max(1, floors - 1))} className="p-3 border border-gray-200 rounded-l-lg bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors" data-id="element-1452">
                        <Minus className="w-5 h-5" data-id="element-1453" />
                      </button>
                      <input type="number" value={floors} readOnly className="w-full border-y border-gray-200 py-3 text-center bg-white text-[#1a2744] font-medium focus:outline-none" data-id="element-1454" />
                      <button type="button" onClick={() => setFloors(floors + 1)} className="p-3 border border-gray-200 rounded-r-lg bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors" data-id="element-1455">
                        <Plus className="w-5 h-5" data-id="element-1456" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-1457">
                    <label className={labelClass} data-id="element-1458">
                      Total Square feet? (incl. Basement)
                    </label>
                    <div className="relative" data-id="element-1459">
                      <select value={sqFeet} onChange={e => setSqFeet(e.target.value)} className={selectClass} data-id="element-1460">
                        <option data-id="element-1461">less than 1600</option>
                        <option data-id="element-1462">1600-2500</option>
                        <option data-id="element-1463">2500-3500</option>
                        <option data-id="element-1464">3500+</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1465" />
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-1466">
                    <label className={labelClass} data-id="element-1467">
                      How do we access the premises?
                    </label>
                    <div className="relative" data-id="element-1468">
                      <select value={access} onChange={e => setAccess(e.target.value)} className={selectClass} data-id="element-1469">
                        <option data-id="element-1470">I will meet you at the location</option>
                        <option data-id="element-1471">Key under mat</option>
                        <option data-id="element-1472">Lock box</option>
                        <option data-id="element-1473">Other</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1474" />
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-1475">
                    <label className={labelClass} data-id="element-1476">Status of the home...</label>
                    <div className="relative" data-id="element-1477">
                      <select value={homeStatus} onChange={e => setHomeStatus(e.target.value)} className={selectClass} data-id="element-1478">
                        <option data-id="element-1479">Completely Empty</option>
                        <option data-id="element-1480">Partially Furnished</option>
                        <option data-id="element-1481">Fully Furnished</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1482" />
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-1483">
                    <label className={labelClass} data-id="element-1484">Parking</label>
                    <PillGroup options={['Free on street', 'Free home parking', 'Parking meters', 'Paid parking lot']} selected={parking} onSelect={setParking} data-id="element-1485" />
                  </div>
                </div>
              </div>

              {/* STEP 4: Extras */}
              <div className={cardClass} data-id="element-1486">
                <h3 className={stepHeadingClass} data-id="element-1487">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1488">STEP 4:</span> Extras?
                </h3>
                <p className="text-gray-500 text-sm mt-2 mb-6" data-id="element-1489">
                  Free up more of your precious time for what matters most!
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-1490">
                  {extras.map(extra => {
                  const isSelected = selectedExtras.includes(extra.name);
                  return <button key={extra.name} type="button" onClick={() => toggleExtra(extra.name)} className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#e91e8c] bg-[#e91e8c]/5' : 'border-gray-100 bg-white hover:border-[#e91e8c]/30 hover:bg-gray-50'}`} data-id="element-1491">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors ${isSelected ? 'bg-[#e91e8c] text-white' : 'bg-[#1a2744] text-white'}`} data-id="element-1492">
                          <extra.icon className="w-6 h-6" data-id="element-1493" />
                        </div>
                        <span className="text-sm font-medium text-center text-[#1a2744] leading-tight" data-id="element-1494">
                          {extra.name}
                        </span>
                      </button>;
                })}
                </div>
              </div>

              {/* STEP 5: Date & Time */}
              <div className={cardClass} data-id="element-1495">
                <h3 className={stepHeadingClass} data-id="element-1496">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1497">STEP 5:</span> Pick a
                  date for the cleaning.
                </h3>
                <p className="text-gray-500 text-sm mt-2 mb-6" data-id="element-1498">
                  For recurring services, each one will commence on the same day
                  and time as selected for the first service.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1499">
                  <div className="space-y-2" data-id="element-1500">
                    <label className={labelClass} data-id="element-1501">Date</label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} data-id="element-1502" />
                  </div>
                  <div className="space-y-2" data-id="element-1503">
                    <label className={labelClass} data-id="element-1504">Time</label>
                    <div className="relative" data-id="element-1505">
                      <select value={timeSlot} onChange={e => setTimeSlot(e.target.value)} className={selectClass} data-id="element-1506">
                        <option value="" data-id="element-1507">Select a time</option>
                        {timeSlots.map(slot => <option key={slot} value={slot} data-id="element-1508">
                            {slot}
                          </option>)}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1509" />
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 6: Access details */}
              <div className={cardClass} data-id="element-1510">
                <h3 className={stepHeadingClass} data-id="element-1511">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1512">STEP 6:</span> Access
                  details & other info we should know?
                </h3>
                <p className="text-gray-500 text-sm mt-2 mb-6" data-id="element-1513">
                  If your instructions to access the premise was either; 'Pick
                  up Keys' or 'Lock Box', please include address for pick or the
                  code to the lock box here.
                </p>
                <textarea value={accessDetails} onChange={e => setAccessDetails(e.target.value)} className={`${inputClass} min-h-[120px] resize-y`} placeholder="Any special instructions..." data-id="element-1514" />
              </div>

              {/* STEP 7: Submit */}
              <div className="flex justify-center pt-4" data-id="element-1515">
                <button type="submit" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-12 py-4 rounded-full font-semibold tracking-wide transition-colors shadow-md flex items-center gap-3 text-lg" data-id="element-1516">
                  Book Now <ArrowRight className="w-5 h-5" data-id="element-1517" />
                </button>
              </div>
            </form>
          </motion.div>
        </section>

        {/* Explore Our Services */}
        <section className="w-full bg-white py-20 px-4 md:px-12 border-t border-gray-200" data-id="element-1518">
          <div className="max-w-7xl mx-auto" data-id="element-1519">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16" data-id="element-1520">
              <h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-[#1a2744] tracking-wide" data-id="element-1521">
                EXPLORE OUR <span className="text-[#e91e8c]" data-id="element-1522">SERVICES</span>
              </h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto" data-id="element-1523">
                Discover our full range of professional cleaning solutions
                tailored to your needs.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1524">
              {[{
              title: 'Basic Home Cleaning',
              desc: 'Regular maintenance to keep your home spotless.',
              link: '/basic-home-cleaning',
              icon: Home
            }, {
              title: 'Spring / Deep Cleaning',
              desc: 'Thorough, top-to-bottom detail cleaning.',
              link: '/spring-deep-cleaning',
              icon: Sparkles
            }, {
              title: 'Commercial Cleaning',
              desc: 'Professional cleaning for your business.',
              link: '/commercial-cleaning',
              icon: Building2
            }, {
              title: 'Contact Us',
              desc: 'Get in touch for custom requests.',
              link: '/contact-us',
              icon: Phone
            }].map((service, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.1
            }} data-id="element-1525">
                  <Link to={service.link} className="group block h-full bg-[#f8f9fa] rounded-xl p-8 hover:shadow-md transition-all border border-gray-100 hover:border-[#5bbcd5]" data-id="element-1526">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-6 group-hover:bg-[#5bbcd5]/10 transition-colors" data-id="element-1527">
                      <service.icon className="w-6 h-6 text-[#1a2744] group-hover:text-[#5bbcd5] transition-colors" data-id="element-1528" />
                    </div>
                    <h3 className="font-bebas text-2xl text-[#1a2744] mb-3 tracking-wide group-hover:text-[#e91e8c] transition-colors" data-id="element-1529">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-6 line-clamp-2" data-id="element-1530">
                      {service.desc}
                    </p>
                    <div className="flex items-center text-[#5bbcd5] font-medium text-sm mt-auto" data-id="element-1531">
                      Learn more
                      <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-1532" />
                    </div>
                  </Link>
                </motion.div>)}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1533" />
    </div>;
}